import React, {useEffect, useState} from 'react';
import {Form, Formik} from "formik";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {changeEmailRequestSchema, changePasswordRequestSchema, userLocationSchema, userProfileNotificationsRequestSchema, userProfileRequestSchema} from "../../utils/schemas";
import {useGlobalNotificationMessage} from "../../context/message";
import {changeEmail, editUser, useUserService} from "../../services/user-service";
import {useCategoryService} from "../../services/category-service";
import {getUserData} from "../../modules/auth-persistor";
import Loading from "../../components/loading";
import FormGroup from "../../components/form-group";
import Select from "react-select";
import {useAuth} from "../../context/auth";
import MainLayout from "../../layouts/main";
import {Col, Nav, Row, Toast, ToastBody} from "react-bootstrap";
import Tab from 'react-bootstrap/Tab';
import MapPicker from "../../components/map-picker";
import {Links, LocalStorageKeys} from "../../utils/constants";
import {changePassword, useAuthService} from "../../services/auth-service";
import StripePaymentMethods from "../../components/stripe-payment-methods";
import ChangeUserAvatar from "../../components/change-user-avatar";
import {useNavigate, useParams} from "react-router-dom";

export default function Profile () {
    const [loaded, setLoaded] = useState(false);
    const [model, setModel] = useState(null);
    const [modelLocation, setModelLocation] = useState(null);
    const [newPhotoUrl, setNewPhotoUrl] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState([]);
    const [showDelete, setShowDelete] = useState(false);
    const [showToast, setShowToast] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');

    const {addErrorMessage, addSuccessMessage, removeMessage } = useGlobalNotificationMessage();

    const auth = useAuth();

    const userService = useUserService();
    const categoryService = useCategoryService();
    const authService = useAuthService();
    const navigationService = useNavigate();
    const params = useParams();

    useEffect(()=>{
        if (params.tab) {
            setActiveTab(params.tab);
        }

        let promises = [
            categoryService.getList(),
        ];

        Promise.all(promises).then(async ([categories=[]])=>{
            let catGroups = categories?.groups.map(
                (group)=>{
                    group.label = group.name ? group.name : 'Other';
                    group.options = [...group.categories.map((item)=>{
                        item.label = item.name;
                        item.value = item.id;
                        return item;
                    })];
                    return group;
                }
            )
            setCategoriesList([...catGroups]);
            await reloadUser();
            setLoaded(true);
        }).catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
        });
    }, []);

    const reloadUser = async ()=>{
        return new Promise((resolve, reject)=>{
            userService.getUser(getUserData()?.id)
                .then((user)=>{
                    setModel(user);
                    setModelLocation(user.location || {})
                    setSelectedCategory(user.categories);
                    setNewPhotoUrl(null);
                    resolve(user);
                })
                .catch(error => {
                    HelperService.handleApiErrors(error, addErrorMessage);
                    reject(error);
                });
        })
    }

    const deleteUser = async ()=>{
        if (true === window.confirm('This action is not reversable! Are you sure you want to remove your account and its data?')){
            authService.deleteUser(getUserData()?.id)
                .then(()=>{
                    try {
                        auth.doLogout();
                    } catch(error){
                        console.log(error);
                    }
                })
                .catch(error => {
                    HelperService.handleApiErrors(error, addErrorMessage);
                });
        }
    }

    const changeEmailSubmit = (values, { setSubmitting, setErrors, resetForm }) => {
        removeMessage();
        changeEmail(getUserData()?.id, values.requestEmail)
            .then(async (responseData) => {
                addSuccessMessage('Email change request was successfully sent! Please verify your email address by clicking the link we sent on your new address.');
                resetForm();
                const u = await reloadUser();
                auth.doAppUserLogin(u);
            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                HelperService.handleFormErrors(error, setErrors);
            })
            .finally(()=>{setSubmitting(false);});
    };

    const resendNewEmailConfirmation = () => {
        if (!model.newEmail){
            return;
        }
        removeMessage();
        changeEmail(getUserData()?.id, model.newEmail)
            .then(async (responseData) => {
                addSuccessMessage('Email change request was successfully sent! Please verify your email address by clicking the link we sent on your new address.');
            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
            });
    };

    const changePasswordSubmit = (values, { setSubmitting, setErrors, resetForm }) => {
        removeMessage();
        changePassword(values.actualPassword, values.newPassword)
            .then(async (responseData) => {
                addSuccessMessage('Password was successfully changed!');
                resetForm();
            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                HelperService.handleFormErrors(error, setErrors);
            })
            .finally(()=>{setSubmitting(false);});
    };

    const selectCategory = (items=null)=>{
        setSelectedCategory([...items]);
    };

    function locationChanged(params){
        setModelLocation(params);
    }

    const handleLocationSubmit = (values, { setSubmitting, setErrors }) => {
        //we are basicaly disabling form values as we are resolving location in other way.
        handleSubmit(model, { setSubmitting, setErrors });
    };

    const handleSubmit = (values, { setSubmitting, setErrors }) => {
        removeMessage();
        setSubmitting(true);

        let requestParams = Object.assign({}, model, values);
        requestParams.categories = selectedCategory.map(c=>c.id);
        requestParams.location = modelLocation?.lat ? modelLocation : null;
        requestParams.photo = newPhotoUrl ? newPhotoUrl : auth.currentUser.photo;

        delete requestParams.trainer;
        delete requestParams.paymentInfo;

        editUser(auth.currentUser.id, requestParams)
            .then(async (responseData) => {

                //reload user with new data and propagate to saved profile info trough app.
                const u = await reloadUser();
                auth.doAppUserLogin(u);
                setShowToast(true);
                setNewPhotoUrl(null);

                let phoneVerification = JSON.parse(localStorage.getItem(LocalStorageKeys.PhoneVerificationReturn));
                if (phoneVerification){
                    if (phoneVerification.timestamp < (new Date().getTime() - 1000*60*60)){
                        localStorage.removeItem(LocalStorageKeys.PhoneVerificationReturn);
                    } else {
                        navigationService(phoneVerification.path);
                    }
                }

                let enableNotifications = JSON.parse(localStorage.getItem(LocalStorageKeys.EnableNotificationsReturn));
                if (enableNotifications){
                    if (enableNotifications.timestamp < (new Date().getTime() - 1000*60*60)){
                        localStorage.removeItem(LocalStorageKeys.EnableNotificationsReturn);
                    } else {
                        navigationService(enableNotifications.path);
                    }
                }

            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                HelperService.handleFormErrors(error, setErrors);
            })
            .finally(()=>{setSubmitting(false);});
    };

    return (
        <>
            <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding-first">
                <div className={"container"}>
                    <div className={"row"}>
                        <div className={"col-sm-12 col-md-7 col-lg-6"}>
                        <h1 className="header-stripe-blue">Profile</h1>
                            <p>Your information and settings</p>
                        </div>
                    </div>
                </div>
            </Section>

            {!loaded && <Loading className="m-5"/>}

            {loaded && <Section animation={HelperService.getRandomAnimation()} className="pb-5">

                <div className="container position-relative pb-5">
                    <div className="row">
                        <div className="col-sm-12 fixed-tabs-nav">
                            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                <Row>
                                    <Col sm={12} md={3}>
                                        <Nav variant="pills" fill className="flex-column">
                                            <Nav.Item>
                                                <Nav.Link eventKey="profile">Edit user information</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="location">Location</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="notifications">Notifications</Nav.Link>
                                            </Nav.Item>
                                            {model.email && <Nav.Item>
                                                <Nav.Link eventKey="changeEmail">Change email</Nav.Link>
                                            </Nav.Item>}
                                            <Nav.Item>
                                                <Nav.Link eventKey="changePassword">Change password</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="paymentMethods">Manage payment methods</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="removeAccount">Remove account</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="guides">Guides</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="policies">Policies</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    </Col>
                                    <Col sm={12} md={9}>
                                        <Tab.Content>
                                            <Tab.Pane eventKey="profile">

                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        {!model && <Loading className="m-2"/>}

                                                        {model && <Formik
                                                            enableReinitialize={true}
                                                            initialValues={userProfileRequestSchema.cast(model)}
                                                            validationSchema={userProfileRequestSchema}
                                                            onSubmit={handleSubmit}
                                                        >
                                                            {({
                                                                  isSubmitting,
                                                                  errors,
                                                                  touched,
                                                                  setFieldValue
                                                              }) => (
                                                                <Form className="row">

                                                                    <div className="mb-3 col-sm-12 d-flex align-items-center gap-2">
                                                                        <ChangeUserAvatar user={model} callback={(res) => setNewPhotoUrl(res.url)}/>
                                                                        {newPhotoUrl && <>
                                                                            <span className="d-inline-block mdi mdi-transfer-right text-secondary"></span>
                                                                            <div className="position-relative" style={{width: '80px', height: '80px'}}>
                                                                                <span
                                                                                    className="rounded-2 shadow p-1 d-inline-block position-absolute mdi mdi-cancel hover-text-danger bg-white clickable"
                                                                                    style={{top: '-5px', right: '-5px', zIndex: 1}}
                                                                                    onClick={() => {
                                                                                        setNewPhotoUrl(null)
                                                                                    }}></span>
                                                                                <div className="rounded-circle overflow-hidden img-fit-wrap" style={{width: '80px', height: '80px'}}>
                                                                                    <img className="img-fit" src={newPhotoUrl} alt=""/>
                                                                                </div>
                                                                            </div>
                                                                        </>}
                                                                    </div>


                                                                    <FormGroup
                                                                        name="name"
                                                                        placeholder="First name"
                                                                        label="Name"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={userProfileRequestSchema}
                                                                    />

                                                                    <FormGroup
                                                                        name="surname"
                                                                        placeholder="last name"
                                                                        label="Last name"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={userProfileRequestSchema}
                                                                    />

                                                                    <FormGroup
                                                                        name="mobile"
                                                                        placeholder="Number with area code"
                                                                        label="Mobile"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={userProfileRequestSchema}
                                                                    />

                                                                    <hr/>

                                                                    <div className="form-group pb-3">
                                                                        <label htmlFor="categories"
                                                                               className="form-label">What fitness
                                                                            activities are you
                                                                            interested in?</label>
                                                                        <Select
                                                                            options={categoriesList}
                                                                            placeholder={'Choose category'}
                                                                            closeMenuOnSelect={false}
                                                                            isSearchable
                                                                            isClearable
                                                                            isMulti
                                                                            value={selectedCategory?.map(
                                                                                (item) => {
                                                                                    item.value = item.id;
                                                                                    item.label = item.name;
                                                                                    return item;
                                                                                }
                                                                            )}
                                                                            onChange={selectCategory}
                                                                        />
                                                                    </div>

                                                                    <div className={"text-center text-md-start mt-2"}>
                                                                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>Confirm</button>
                                                                        <Toast className="d-inline-block shadow-none border-0 ms-2 text-success bg-white" onClose={() => setShowToast(null)}
                                                                               show={showToast} delay={3000} autohide>
                                                                            <ToastBody><span className="mdi mdi-check"></span> Saved!</ToastBody>
                                                                        </Toast>
                                                                    </div>
                                                                </Form>
                                                            )}
                                                        </Formik>}
                                                    </div>
                                                </div>

                                            </Tab.Pane>
                                            <Tab.Pane eventKey="location">

                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        {!model && <Loading className="m-2"/>}

                                                        {model && modelLocation && <Formik
                                                            enableReinitialize={true}
                                                            initialValues={userLocationSchema.cast(model)}
                                                            onSubmit={handleLocationSubmit}
                                                        >
                                                            {({
                                                                  isSubmitting,
                                                                  errors,
                                                                  touched,
                                                                  setFieldValue
                                                              }) => (
                                                                <Form className="row">
                                                                    <div className="form-group pb-3">
                                                                        <label htmlFor="location" className="form-label">Change your location</label>
                                                                        <div>
                                                                        <MapPicker
                                                                                onChange={(data) => locationChanged(data)}
                                                                                lat={modelLocation?.lat}
                                                                                lon={modelLocation?.lon}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="form-group col-sm-4 pb-3">
                                                                        <label htmlFor="location" className="form-label">Address</label>
                                                                        <div>{modelLocation?.address}</div>
                                                                    </div>

                                                                    <div className="form-group col-sm-4 pb-3">
                                                                        <label htmlFor="country" className="form-label">City</label>
                                                                        <div>{modelLocation?.city}</div>
                                                                    </div>

                                                                    <div className={"text-center text-md-start mt-2"}>
                                                                        <button type="submit"
                                                                                className="btn btn-primary"
                                                                                disabled={isSubmitting}>Confirm
                                                                        </button>
                                                                        <Toast className="d-inline-block shadow-none border-0 ms-2 text-success bg-white" onClose={() => setShowToast(null)}
                                                                               show={showToast} delay={3000} autohide>
                                                                            <ToastBody><span className="mdi mdi-check"></span> Saved!</ToastBody>
                                                                        </Toast>
                                                                    </div>
                                                                </Form>
                                                            )}
                                                        </Formik>}
                                                    </div>
                                                </div>

                                            </Tab.Pane>
                                            <Tab.Pane eventKey="notifications">

                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        {!model && <Loading className="m-2"/>}

                                                        {model && <Formik
                                                            enableReinitialize={true}
                                                            initialValues={userProfileNotificationsRequestSchema.cast(model)}
                                                            validationSchema={userProfileNotificationsRequestSchema}
                                                            onSubmit={handleSubmit}
                                                        >
                                                            {({
                                                                  isSubmitting,
                                                                  errors,
                                                                  touched,
                                                              }) => (
                                                                <Form className="row">
                                                                    <FormGroup
                                                                        name="emailNotifications"
                                                                        type="checkbox"
                                                                        placeholder="enable/disable"
                                                                        label="Notifications"
                                                                        checkLabel="Enable email notifications"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={userProfileNotificationsRequestSchema}
                                                                    />
                                                                    <div className={"text-center text-md-start mt-2"}>
                                                                        <button type="submit"
                                                                                className="btn btn-primary"
                                                                                disabled={isSubmitting}>Confirm
                                                                        </button>
                                                                        <Toast className="d-inline-block shadow-none border-0 ms-2 text-success bg-white" onClose={()=>setShowToast(null)} show={showToast} delay={3000} autohide>
                                                                            <ToastBody><span className="mdi mdi-check"></span> Saved!</ToastBody>
                                                                        </Toast>
                                                                    </div>
                                                                </Form>
                                                            )}
                                                        </Formik>}
                                                    </div>
                                                </div>

                                            </Tab.Pane>
                                            <Tab.Pane eventKey="changePassword">

                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        {!model && <Loading className="m-2"/>}

                                                        {model && <Formik
                                                            enableReinitialize={true}
                                                            initialValues={changePasswordRequestSchema.cast()}
                                                            validationSchema={changePasswordRequestSchema}
                                                            onSubmit={changePasswordSubmit}
                                                        >
                                                            {({
                                                                  isSubmitting,
                                                                  errors,
                                                                  touched,
                                                                  setfieldValue,
                                                              }) => (
                                                                <Form className="row">
                                                                    <FormGroup
                                                                        name="actualPassword"
                                                                        type="password"
                                                                        autoComplete="current-password"
                                                                        placeholder="Current password"
                                                                        label="Current password"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={changePasswordRequestSchema}
                                                                    />
                                                                    <FormGroup
                                                                        name="newPassword"
                                                                        type="password"
                                                                        placeholder="New password"
                                                                        autoComplete="new-password"
                                                                        label="New password"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={changePasswordRequestSchema}
                                                                    />
                                                                    <FormGroup
                                                                        name="confirmNewPassword"
                                                                        type="password"
                                                                        placeholder="New password"
                                                                        autoComplete="new-password"
                                                                        label="New password"
                                                                        className="mb-3 col-md-4"
                                                                        touched={touched} errors={errors}
                                                                        schema={changePasswordRequestSchema}
                                                                    />
                                                                    <div className={"text-center text-md-start mt-2"}>
                                                                        <button type="submit"
                                                                                className="btn btn-primary"
                                                                                disabled={isSubmitting}>Confirm
                                                                        </button>
                                                                        <Toast className="d-inline-block shadow-none border-0 ms-2 text-success bg-white" onClose={()=>setShowToast(null)} show={showToast} delay={3000} autohide>
                                                                            <ToastBody><span className="mdi mdi-check"></span> Saved!</ToastBody>
                                                                        </Toast>
                                                                    </div>
                                                                </Form>
                                                            )}
                                                        </Formik>}
                                                    </div>
                                                </div>

                                            </Tab.Pane>
                                            <Tab.Pane eventKey="changeEmail">

                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        {!model && <Loading className="m-2"/>}

                                                        {model && <Formik
                                                            enableReinitialize={true}
                                                            initialValues={changeEmailRequestSchema.cast({email: model.email})}
                                                            validationSchema={changeEmailRequestSchema}
                                                            onSubmit={changeEmailSubmit}
                                                        >
                                                            {({
                                                                  isSubmitting,
                                                                  errors,
                                                                  touched,
                                                              }) => (
                                                                <Form className="row">
                                                                    <FormGroup
                                                                        name="email"
                                                                        autoComplete="email"
                                                                        placeholder="Current email address"
                                                                        label="Current email address"
                                                                        className="mb-3 col-md-6"
                                                                        touched={touched} errors={errors}
                                                                        schema={changeEmailRequestSchema}
                                                                    />
                                                                    <FormGroup
                                                                        name="requestEmail"
                                                                        autoComplete="new-email"
                                                                        placeholder="New email address"
                                                                        label="New email address"
                                                                        className="mb-3 col-md-6"
                                                                        touched={touched} errors={errors}
                                                                        schema={changeEmailRequestSchema}
                                                                    />
                                                                    {model.newEmail && <p className="my-3">
                                                                        Process of changing your email address is in progress.
                                                                        You just need to verify your email by clicking on the link in the message we sent you.
                                                                        Didn't get the email? <a className="clickable" onClick={()=>{resendNewEmailConfirmation();}}>Resend the verification</a> email to <strong>{model.newEmail}</strong>.
                                                                    </p>}
                                                                    <p className="my-3">This email address will be shown in your profile and also will be used for signing into your account.</p>
                                                                    <div className={"text-center text-md-start mt-2"}>
                                                                        <button type="submit"
                                                                                className="btn btn-primary"
                                                                                disabled={isSubmitting}>Confirm
                                                                        </button>
                                                                        <Toast className="d-inline-block shadow-none border-0 ms-2 text-success bg-white" onClose={()=>setShowToast(null)} show={showToast} delay={3000} autohide>
                                                                            <ToastBody><span className="mdi mdi-check"></span> Saved!</ToastBody>
                                                                        </Toast>
                                                                    </div>
                                                                </Form>
                                                            )}
                                                        </Formik>}
                                                    </div>
                                                </div>

                                            </Tab.Pane>
                                            <Tab.Pane eventKey="removeAccount">
                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        {!showDelete && <>
                                                            <p>By removing your account, all the data associated
                                                                with your profile will be permanently deleted. You
                                                                won't be able to sign in again and access your
                                                                profile.</p>
                                                            <button className="btn btn-danger" onClick={() => {
                                                                setShowDelete(true);
                                                            }}>Remove my account
                                                            </button>
                                                        </>}

                                                        {showDelete && <>
                                                            <div>
                                                                <div
                                                                    className="mdi mdi-delete-circle-outline mb-2 text-danger"
                                                                    style={{'fontSize': '50px'}}></div>
                                                                <h4>We're sorry to see you go</h4>
                                                                <p>However, you can still <a className="clickable" onClick={() => {
                                                                    setShowDelete(false);
                                                                }}>change your mind</a>.</p>
                                                                <p>By removing your account, all the data associated
                                                                    with your profile will be permanently deleted. You
                                                                    won't be able to sign in again and access your
                                                                    profile.</p>
                                                                <p><span className="text-danger">Warning</span>: this
                                                                    process is irreversible!</p>
                                                                <button className="btn btn-danger" onClick={() => {
                                                                    deleteUser();
                                                                }}>Remove my account
                                                                </button>
                                                            </div>
                                                        </>}

                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="paymentMethods">
                                                <div className="card">
                                                    <div className="card-body p-4">

                                                        <StripePaymentMethods />

                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="guides">
                                                <div className="card border-0">
                                                    <div className="card-body p-4">
                                                        <ul className="list-group">
                                                            <li className="list-group-item p-3"><a target="_blank" rel="noreferrer" className="stretched-link" href={Links.paymentsGuide}>Payments user guide</a></li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="policies">
                                                <div className="card border-0">
                                                    <div className="card-body p-4">
                                                        <ul className="list-group">
                                                            <li className="list-group-item p-3"><a target="_blank" rel="noreferrer" className="stretched-link" href={Links.terms}>Terms of use</a></li>
                                                            <li className="list-group-item p-3"><a target="_blank" rel="noreferrer" className="stretched-link" href={Links.eula}>End-User License Agreement</a></li>
                                                            <li className="list-group-item p-3"><a target="_blank" rel="noreferrer" className="stretched-link" href={Links.privacyPolicy}>Privacy policy</a></li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Col>
                                </Row>
                            </Tab.Container>


                        </div>
                    </div>
                </div>

            </Section>}
        </>
    );
}


Profile.getLayout = function getLayout(page) {
    return (
        <MainLayout>
            {page}
        </MainLayout>
    )
}
